import { ComboBoxItemType } from '@appcues/sonar';
import { getTimeZones, TimeZone } from '@vvo/tzdb';

export type WindowType = Record<string, Array<{ start: string; end: string }>>;

export const DAY_OPTIONS = [
  { label: 'Sunday', value: 'sunday' },
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
] as ComboBoxItemType[];

const TIMEZONES: Record<string, string> = {
  'Pacific/Honolulu': 'Hawaii',
  'America/Anchorage': 'Alaska',
  'America/Los_Angeles': 'Pacific Time (US & Canada)',
  'America/Denver': 'Mountain Time (US & Canada)',
  'America/Phoenix': 'Arizona',
  'America/Chicago': 'Central Time (US & Canada)',
  'America/Mexico_City': 'Mexico City',
  'America/New_York': 'Eastern Time (US & Canada)',
  'America/Toronto': 'Eastern Time (Canada)',
  'America/Halifax': 'Atlantic Time (Canada)',
  'America/Caracas': 'Caracas',
  'America/Sao_Paulo': 'Sao Paulo',
  'America/Argentina/Buenos_Aires': 'Buenos Aires',
  'Atlantic/South_Georgia': 'Mid-Atlantic',
  'Atlantic/Azores': 'Azores',
  'Atlantic/Cape_Verde': 'Cape Verde',
  'Europe/London': 'London',
  'Europe/Lisbon': 'Lisbon',
  'Atlantic/Reykjavik': 'Reykjavik',
  'Europe/Rome': 'Rome',
  'Europe/Paris': 'Paris',
  'Europe/Madrid': 'Madrid',
  'Europe/Berlin': 'Berlin',
  'Europe/Warsaw': 'Warsaw',
  'Europe/Athens': 'Athens',
  'Europe/Prague': 'Prague',
  'Africa/Cairo': 'Cairo',
  'Asia/Jerusalem': 'Jerusalem',
  'Africa/Johannesburg': 'Johannesburg',
  'Asia/Dubai': 'Dubai',
  'Europe/Moscow': 'Moscow',
  'Asia/Bangkok': 'Bangkok',
  'Asia/Jakarta': 'Jakarta',
  'Asia/Singapore': 'Singapore',
  'Asia/Shanghai': 'Beijing',
  'Asia/Hong_Kong': 'Hong Kong',
  'Asia/Tokyo': 'Tokyo',
  'Asia/Seoul': 'Seoul',
  'Australia/Sydney': 'Sydney',
  'Australia/Brisbane': 'Brisbane',
  'Pacific/Fiji': 'Fiji',
};

/**
 * Formats a timezone object into a label and value pair.
 * Show DST updated labels for timezones in both summer and winter
 * For any users outside of DST (or anywhere in the world) will see the DST updated timezone
 *
 * @param timezone - The timezone object to format.
 * @returns An object containing the formatted label and value.
 */
const formatTimezone = (timezone: TimeZone) => {
  const offset = timezone.currentTimeOffsetInMinutes;
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  const utcOffset = `(UTC${sign}${hours}:${minutes})`;

  return {
    label: `${utcOffset} ${TIMEZONES[timezone.name] || timezone.name}`,
    value: timezone.name,
  };
};

export const getDSTAwareTimezones = () => {
  const preferredTimezones = getTimeZones().filter(timezone =>
    Object.keys(TIMEZONES).includes(timezone.name)
  );
  const handledTimezones = preferredTimezones
    .map(timezone => formatTimezone(timezone))
    .sort((a, b) => a.label.localeCompare(b.label));

  return handledTimezones;
};

export const TIMEZONE_OPTIONS = getDSTAwareTimezones();

export const DEFAULT_TIMEZONE = 'Europe/London';

export const RECIPIENT_TIMEZONE_OPTION = {
  label: "Recipient's time zone",
  value: 'recipient-time-zone',
};
